import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";
import { X } from "lucide-react";
import { toast } from "sonner";

import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { Message, MessageContent } from "@/components/ai-elements/message";
import {
  PromptInput,
  PromptInputTextarea,
  PromptInputFooter,
  PromptInputSubmit,
} from "@/components/ai-elements/prompt-input";
import { Shimmer } from "@/components/ai-elements/shimmer";
import { EditorialMarkdown } from "@/components/buddy/EditorialMarkdown";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/trackEvent";
import { OPEN_CHAT_EVENT } from "@/components/FloatingActionMenu";
import logo from "@/assets/gotobuddy-logo.png";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_KEY = (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ||
  import.meta.env.VITE_SUPABASE_ANON_KEY) as string;

const GREETING =
  "Hey! I can review any coin for you in seconds or answer crypto questions. What coin are you looking at?";

/** Routes where the launcher should stay hidden (dedicated chat / admin surfaces). */
const HIDDEN_PREFIXES = ["/buddy", "/admin"];

const greetingMessage = (): UIMessage => ({
  id: "greeting",
  role: "assistant",
  parts: [{ type: "text", text: GREETING }],
});

export function ChatWidget() {
  const location = useLocation();
  const hidden = HIDDEN_PREFIXES.some((p) => location.pathname.startsWith(p));

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");


  const transport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${SUPABASE_URL}/functions/v1/gotobuddy-chat`,
        headers: { Authorization: `Bearer ${SUPABASE_KEY}` },
      }),
    [],
  );

  const { messages, sendMessage, status, stop } = useChat({
    id: "gotocoins-chat-widget",
    messages: [greetingMessage()],
    transport,
    onError: (err) => toast.error(err.message || "The assistant hit a snag"),
  });

  const isLoading = status === "submitted" || status === "streaming";

  const openChat = (source: string) => {
    setOpen(true);
    trackEvent("chatbot_opened", { source, path: location.pathname });
  };

  // Opened from the single floating action menu.
  useEffect(() => {
    if (hidden) return;
    const handler = (e: Event) => openChat((e as CustomEvent).detail ?? "floating_menu");
    window.addEventListener(OPEN_CHAT_EVENT, handler);
    return () => window.removeEventListener(OPEN_CHAT_EVENT, handler);
  }, [hidden, location.pathname]);

  useEffect(() => {
    if (open) {
      setTimeout(
        () => document.querySelector<HTMLTextAreaElement>('[role="dialog"] textarea')?.focus(),
        80,
      );
    }
  }, [open]);


  const submit = async (raw?: string) => {
    const text = (raw ?? input).trim();
    if (!text || isLoading) return;
    setInput("");
    trackEvent("chatbot_message_sent", { path: location.pathname, length: text.length });
    if (/\b(review|analyz|analys|deep dive|rate|worth buying)\b/i.test(text)) {
      trackEvent("chatbot_coin_review_requested", { path: location.pathname, query: text.slice(0, 120) });
    }
    await sendMessage({ text });
  };

  if (hidden) return null;

  return (
    <>
      {/* Panel */}
      {open && (
        <div
          role="dialog"
          aria-label="Crypto AI assistant"
          className="fixed z-[60] bg-card border border-border/70 shadow-2xl flex flex-col
            inset-x-0 bottom-0 top-16 rounded-t-2xl
            sm:inset-auto sm:bottom-24 sm:right-4 sm:top-auto sm:w-[380px] sm:h-[560px] sm:rounded-2xl"
        >
          <div className="flex items-center gap-2 px-4 py-3 border-b border-border/60">
            <img src={logo} alt="" width={28} height={28} className="rounded-md" />
            <div className="leading-tight">
              <div className="text-sm font-semibold">Crypto AI</div>
              <div className="text-[11px] text-muted-foreground">Coin reviews & crypto questions</div>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              className="ml-auto"
              aria-label="Close chat"
              onClick={() => setOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          <Conversation className="flex-1 min-h-0">
            <ConversationContent className="px-3 py-3">
              {messages.map((m) => {
                const text = m.parts.map((p) => (p.type === "text" ? p.text : "")).join("");
                return (
                  <Message key={m.id} from={m.role === "user" ? "user" : "assistant"}>
                    <MessageContent>
                      {m.role === "user" ? (
                        <p className="whitespace-pre-wrap">{text}</p>
                      ) : (
                        <EditorialMarkdown content={text} />
                      )}
                    </MessageContent>
                  </Message>
                );
              })}
              {status === "submitted" && (
                <Message from="assistant">
                  <MessageContent>
                    <Shimmer>Thinking…</Shimmer>
                  </MessageContent>
                </Message>
              )}
            </ConversationContent>
            <ConversationScrollButton />
          </Conversation>

          {messages.length <= 1 && (
            <div className="flex flex-wrap gap-2 px-3 pb-1">
              {["Review BTC", "Best AU exchange?", "Find me a low-cap gem"].map((p) => (
                <Button key={p} variant="outline" size="sm" onClick={() => submit(p)} disabled={isLoading}>
                  {p}
                </Button>
              ))}
            </div>
          )}

          <div className="p-3">
            <PromptInput
              onSubmit={(_msg, e) => {
                e.preventDefault();
                void submit();
              }}
            >
              <PromptInputTextarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about any coin…"
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    void submit();
                  }
                }}
              />
              <PromptInputFooter className="justify-end">
                <PromptInputSubmit
                  status={status}
                  disabled={!input.trim() && !isLoading}
                  onClick={(e) => {
                    if (isLoading) {
                      e.preventDefault();
                      stop();
                    }
                  }}
                />
              </PromptInputFooter>
            </PromptInput>
          </div>
        </div>
      )}
    </>
  );
}


export default ChatWidget;
